/**
 * Browser-tab unread indicator.
 *
 * On the web the user is usually looking at a *different* tab when a message
 * arrives, so the in-app red dot on the Chats icon is invisible to them. These
 * helpers move the signal out to the parts of the browser chrome that stay on
 * screen: the tab title, the favicon, and (with permission) an OS notification.
 *
 * Everything here is a plain function over `document` / `window` so it can be
 * driven from tests without mounting React. The React wiring lives in
 * `components/TabNotifier.jsx`.
 */

// The title the app carries when there is nothing unread. Captured once, on
// first use, so repeated badge updates never compound into "(2) (1) Astreona".
let baseTitle = null;

export const DEFAULT_TITLE = 'Astreona';

/**
 * Forget everything captured from the page.
 *
 * Called when the provider unmounts. The captured base title and favicon href
 * describe *this* page; keeping them across a logout/login would restore a
 * stale icon or title onto the next one.
 */
export function resetTabAlert() {
    baseTitle = null;
    originalFaviconHref = null;
    badgedFaviconHref = null;
    stopTitleFlash();
}

function getBaseTitle() {
    if (baseTitle === null) {
        baseTitle = stripBadge(document.title) || DEFAULT_TITLE;
    }
    return baseTitle;
}

/** Strip a previously applied "(n) " prefix so re-badging is idempotent. */
export function stripBadge(title) {
    return String(title || '').replace(/^\(\d+\+?\)\s*/, '');
}

/**
 * Render the unread count into the tab title.
 *
 * `count` is the number of unread *messages*, not conversations — it is what
 * the user is being asked to come back for. Counts above 99 are shown as
 * "(99+)" so the title never pushes the site name out of the visible tab.
 */
export function setTitleBadge(count) {
    const base = getBaseTitle();
    if (!count || count <= 0) {
        document.title = base;
        return base;
    }
    const label = count > 99 ? '99+' : String(count);
    document.title = `(${label}) ${base}`;
    return document.title;
}

// ---------------------------------------------------------------------------
// Favicon
// ---------------------------------------------------------------------------

// The site's own favicon href, captured before we overwrite it.
let originalFaviconHref = null;
let badgedFaviconHref = null;

function findFaviconLink() {
    let link = document.querySelector("link[rel~='icon']");
    if (!link) {
        link = document.createElement('link');
        link.rel = 'icon';
        document.head.appendChild(link);
    }
    return link;
}

/**
 * Overlay a red dot on the favicon.
 *
 * Drawing the real favicon into a canvas and stamping a dot on it needs the
 * image to load, which is asynchronous and fails outright for a cross-origin
 * icon. Since the dot only has to read as "something is waiting", we skip the
 * source image and draw the badge as a standalone mark — that works
 * synchronously, everywhere, and degrades to a no-op if canvas is unavailable
 * (jsdom, very old browsers).
 */
export function setFaviconBadge(show) {
    const link = findFaviconLink();
    if (originalFaviconHref === null) {
        originalFaviconHref = link.getAttribute('href') || '';
    }

    if (!show) {
        link.setAttribute('href', originalFaviconHref);
        return originalFaviconHref;
    }

    if (!badgedFaviconHref) {
        badgedFaviconHref = drawBadgedFavicon();
    }
    if (badgedFaviconHref) {
        link.setAttribute('href', badgedFaviconHref);
    }
    return badgedFaviconHref;
}

function drawBadgedFavicon() {
    try {
        const canvas = document.createElement('canvas');
        canvas.width = 32;
        canvas.height = 32;
        const ctx = canvas.getContext('2d');
        if (!ctx) return null;

        // Chat bubble in the brand blue, so the tab still looks like the app.
        ctx.fillStyle = '#2563eb';
        ctx.beginPath();
        ctx.arc(15, 14, 11, 0, Math.PI * 2);
        ctx.fill();

        // Unread dot, offset to the corner where a badge is expected.
        ctx.fillStyle = '#ffffff';
        ctx.beginPath();
        ctx.arc(24, 8, 8, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = '#ef4444';
        ctx.beginPath();
        ctx.arc(24, 8, 6.5, 0, Math.PI * 2);
        ctx.fill();

        return canvas.toDataURL('image/png');
    } catch {
        return null;
    }
}

// ---------------------------------------------------------------------------
// Title flash
// ---------------------------------------------------------------------------

let flashTimer = null;
let flashOn = false;

/**
 * Alternate the tab title between the badge and an attention message.
 *
 * Only worth doing while the tab is hidden: a title that blinks under the
 * user's nose while they are reading the chat is just noise.
 */
export function startTitleFlash(message, count, intervalMs = 1500) {
    stopTitleFlash();
    if (!message) return;
    // Capture the real title *before* the first tick overwrites it, or the
    // flash message itself becomes the base title and the badge ends up
    // reading "(2) 💬 New message!".
    getBaseTitle();
    flashTimer = setInterval(() => {
        flashOn = !flashOn;
        if (flashOn) {
            document.title = message;
        } else {
            setTitleBadge(count);
        }
    }, intervalMs);
}

export function stopTitleFlash() {
    if (flashTimer) {
        clearInterval(flashTimer);
        flashTimer = null;
    }
    flashOn = false;
}

export function isFlashing() {
    return flashTimer !== null;
}

// ---------------------------------------------------------------------------
// OS-level notification
// ---------------------------------------------------------------------------

export function notificationPermission() {
    if (typeof window === 'undefined' || !('Notification' in window)) return 'unsupported';
    return window.Notification.permission;
}

/**
 * Ask for desktop-notification permission.
 *
 * Browsers reject a permission prompt that is not tied to a user gesture, and
 * a prompt on page load trains the user to click "Block". Call this from a
 * click — the settings toggle — not from an effect.
 */
export async function requestNotificationPermission() {
    if (notificationPermission() === 'unsupported') return 'unsupported';
    try {
        return await window.Notification.requestPermission();
    } catch {
        return window.Notification.permission;
    }
}

/**
 * Raise an OS notification for a new message.
 *
 * `tag` collapses repeats: ten messages from the same person replace one
 * notification instead of stacking ten. Returns the Notification, or null when
 * it was not shown (unsupported, not granted, tab in the foreground).
 */
export function showDesktopNotification({ title, body, tag, onClick }) {
    if (notificationPermission() !== 'granted') return null;
    if (typeof document !== 'undefined' && document.visibilityState === 'visible') return null;
    try {
        const notification = new window.Notification(title, { body, tag, renotify: false });
        notification.onclick = () => {
            try {
                window.focus();
            } catch {
                /* focus is best-effort */
            }
            if (onClick) onClick();
            notification.close();
        };
        return notification;
    } catch {
        return null;
    }
}

/**
 * Apply the whole tab state in one call.
 *
 * Kept as a single entry point so the title, the favicon and the flash can
 * never disagree with each other — every caller sets all three together.
 */
export function applyTabAlert({ count, hidden, flashMessage }) {
    const unread = Math.max(0, Number(count) || 0);
    setTitleBadge(unread);
    setFaviconBadge(unread > 0);

    if (unread > 0 && hidden && flashMessage) {
        startTitleFlash(flashMessage, unread);
    } else {
        stopTitleFlash();
        setTitleBadge(unread);
    }
    return unread;
}
